import React, { useState, useEffect } from 'react';
import { SearchableSelect } from './DynamicTableViewerV2/SearchableSelect';

export const VirtualSchemaManager = ({ token, instance, ipAddress, databaseName, tableName, schemaGroup }) => {
    const [relationships, setRelationships] = useState([]);
    const [virtualKeys, setVirtualKeys] = useState([]);
    const [activeTab, setActiveTab] = useState('keys'); // 'keys' or 'rels'
    const [loading, setLoading] = useState(false);
    const [schemaError, setSchemaError] = useState(null);

    // Metadata cache for schema helper dropdowns
    const [tablesList, setTablesList] = useState([]);
    const [columnsMap, setColumnsMap] = useState({}); // tableName -> array of columns
    const [selectedTableForKeys, setSelectedTableForKeys] = useState(tableName || '');
    const [selectedTableForRelsSource, setSelectedTableForRelsSource] = useState(tableName || '');
    const [selectedTableForRelsTarget, setSelectedTableForRelsTarget] = useState('');

    // Form states
    const [newKey, setNewKey] = useState({ columnName: '', isPrimaryKey: false, isUnique: false });
    
    // Multi-column relationship builder
    const [columnPairs, setColumnPairs] = useState([{ source: '', target: '' }]);
    const [description, setDescription] = useState('');
    const [suggestedTargetCol, setSuggestedTargetCol] = useState('');

    useEffect(() => {
        if (tableName) {
            setSelectedTableForKeys(tableName);
            setSelectedTableForRelsSource(tableName);
            fetchColumns(tableName);
        }
    }, [tableName]);

    const fetchData = async () => {
        if (!instance || !databaseName || !schemaGroup) return;
        setLoading(true);
        const headers = { 'Authorization': `Bearer ${token}` };
        const prefix = `/api/virtual-schema`;
        try {
            const [relRes, keysRes] = await Promise.all([
                fetch(`${prefix}/relationships?schemaGroup=${encodeURIComponent(schemaGroup)}`, { headers }),
                fetch(`${prefix}/keys?schemaGroup=${encodeURIComponent(schemaGroup)}`, { headers })
            ]);

            if (relRes.ok) setRelationships(await relRes.json());
            if (keysRes.ok) setVirtualKeys(await keysRes.json());
        } catch (err) {
            console.error("Failed to load virtual schema data", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        loadSchemaTemplate();
    }, [token, instance, databaseName, ipAddress]);

    const loadSchemaTemplate = async () => {
        if (!instance || !databaseName) return;
        setSchemaError(null);
        setTablesList([]);
        setColumnsMap({});

        const headers = { 'Authorization': `Bearer ${token}` };
        try {
            const res = await fetch(`/api/remote-sql/${instance.InstanceID}/${ipAddress}/${databaseName}/schema/tables`, { headers });

            if (res.ok) {
                const data = await res.json();
                const tables = Array.isArray(data) ? data.map(t => t.TableName) : [];
                setTablesList(tables);
            } else {
                const errData = await res.json().catch(() => ({}));
                throw new Error(errData.error || `HTTP ${res.status}`);
            }
        } catch (e) {
            console.error("Failed to load schema tables", e);
            setSchemaError(`Failed to load tables list: ${e.message}.`);
        }
    };

    const fetchColumns = async (fullTableName) => {
        if (!fullTableName || columnsMap[fullTableName]) return;
        const [schema, table] = fullTableName.split('.');
        if (!schema || !table || !instance || !databaseName) return;

        const headers = { 'Authorization': `Bearer ${token}` };
        try {
            const res = await fetch(`/api/remote-sql/${instance.InstanceID}/${ipAddress}/${databaseName}/schema/metadata/${schema}/${table}`, { headers });

            if (res.ok) {
                const cols = await res.json();
                setColumnsMap(prev => ({ ...prev, [fullTableName]: cols }));
            } else {
                const errData = await res.json().catch(() => ({}));
                throw new Error(errData.error || `HTTP ${res.status}`);
            }
        } catch (err) {
            console.error(`Failed to load columns for ${fullTableName}`, err);
            setSchemaError(`Failed to load columns for ${fullTableName}: ${err.message}`);
        }
    };

    // Auto-suggest UX Assistance when Target Table is selected in Relationships
    useEffect(() => {
        if (!selectedTableForRelsTarget) {
            setSuggestedTargetCol('');
            return;
        }
        
        const [schema, table] = selectedTableForRelsTarget.split('.');
        const targetPks = virtualKeys.filter(vk => 
            vk.SchemaName.toLowerCase() === (schema || 'dbo').toLowerCase() && 
            vk.TableName.toLowerCase() === table.toLowerCase() && 
            vk.IsPrimaryKey
        );

        if (targetPks.length > 0) {
            const suggestedNames = targetPks.map(pk => pk.ColumnName);
            setSuggestedTargetCol(suggestedNames.join(', '));
            
            // Build matching pairs automatically
            setColumnPairs(suggestedNames.map(name => ({ source: '', target: name })));
        } else {
            setSuggestedTargetCol('');
            setColumnPairs([{ source: '', target: '' }]);
        }

        fetchColumns(selectedTableForRelsTarget);
    }, [selectedTableForRelsTarget, virtualKeys]);

    const handleAddKey = async (e) => {
        e.preventDefault();
        const [schema, table] = selectedTableForKeys.split('.');
        if (!schema || !table || !newKey.columnName) {
            alert("Please select a table and column");
            return;
        }

        const headers = { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        };
        try {
            const res = await fetch(`/api/virtual-schema/keys`, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    schemaGroup,
                    schemaName: schema,
                    tableName: table,
                    columnName: newKey.columnName,
                    isPrimaryKey: newKey.isPrimaryKey,
                    isUnique: newKey.isUnique
                })
            });
            if (res.ok) {
                const added = await res.json();
                setVirtualKeys(prev => [...prev, added]);
                setNewKey({ columnName: '', isPrimaryKey: false, isUnique: false });
            } else {
                const err = await res.json();
                alert(`Error: ${err.error}`);
            }
        } catch (e) {
            alert(`Error: ${e.message}`);
        }
    };

    const handleDeleteKey = async (id) => {
        if (!window.confirm("Delete this virtual key definition?")) return;
        const headers = { 'Authorization': `Bearer ${token}` };
        try {
            const res = await fetch(`/api/virtual-schema/keys/${id}`, {
                method: 'DELETE',
                headers
            });
            if (res.ok) {
                setVirtualKeys(prev => prev.filter(k => k.KeyID !== id));
            }
        } catch (e) {
            alert(e.message);
        }
    };

    const handleAddRel = async (e) => {
        e.preventDefault();
        const [srcSchema, srcTable] = selectedTableForRelsSource.split('.');
        const [tgtSchema, tgtTable] = selectedTableForRelsTarget.split('.');
        
        // Ensure all column pairs are fully specified
        const missing = columnPairs.some(p => !p.source || !p.target);
        if (!srcTable || !tgtTable || missing) {
            alert("Please fully specify all source and target columns for the relationship.");
            return;
        }

        const sourceColumn = columnPairs.map(p => p.source.trim()).join(',');
        const targetColumn = columnPairs.map(p => p.target.trim()).join(',');

        const headers = { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        };
        try {
            const res = await fetch(`/api/virtual-schema/relationships`, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    schemaGroup,
                    sourceSchema: srcSchema || 'dbo',
                    sourceTable: srcTable,
                    sourceColumn,
                    targetSchema: tgtSchema || 'dbo',
                    targetTable: tgtTable,
                    targetColumn,
                    description: description
                })
            });
            if (res.ok) {
                const added = await res.json();
                setRelationships(prev => [...prev, added]);
                setColumnPairs([{ source: '', target: '' }]);
                setDescription('');
            } else {
                const err = await res.json();
                alert(`Error: ${err.error}`);
            }
        } catch (e) {
            alert(`Error: ${e.message}`);
        }
    };

    const handleDeleteRel = async (id) => {
        if (!window.confirm("Delete this virtual relationship?")) return;
        const headers = { 'Authorization': `Bearer ${token}` };
        try {
            const res = await fetch(`/api/virtual-schema/relationships/${id}`, {
                method: 'DELETE',
                headers
            });
            if (res.ok) {
                setRelationships(prev => prev.filter(r => r.RelationshipID !== id));
            }
        } catch (e) {
            alert(e.message);
        }
    };

    if (!instance || !databaseName) {
        return (
            <div className="flex-1 flex items-center justify-center text-slate-500 bg-slate-900 h-full">
                Select a database and table to manage virtual schemas.
            </div>
        );
    }

    if (!schemaGroup) {
        return (
            <div className="flex-1 flex flex-col items-center justify-center text-slate-500 bg-slate-900 h-full">
                <span className="material-symbols-outlined text-4xl mb-2 text-slate-600">error</span>
                <p>No Schema Group tag assigned to this database.</p>
                <p className="text-xs text-slate-600 mt-2 max-w-md text-center">To define virtual schemas, please tag this database with a Schema Group in the Remote SQL Explorer header.</p>
            </div>
        );
    }

    return (
        <div className="flex-1 flex flex-col min-h-0 bg-slate-900 text-slate-100 h-full">
            <div className="p-6 border-b border-slate-700 bg-slate-850 flex items-center justify-between">
                <div className="flex space-x-1 bg-slate-800 p-1 rounded border border-slate-700">
                    <button 
                        onClick={() => setActiveTab('keys')}
                        className={`px-4 py-1.5 rounded text-sm transition-colors ${activeTab === 'keys' ? 'bg-blue-600 text-white font-medium shadow' : 'text-slate-400 hover:text-white'}`}
                    >
                        Virtual Keys
                    </button>
                    <button 
                        onClick={() => setActiveTab('rels')}
                        className={`px-4 py-1.5 rounded text-sm transition-colors ${activeTab === 'rels' ? 'bg-blue-600 text-white font-medium shadow' : 'text-slate-400 hover:text-white'}`}
                    >
                        Virtual Relationships
                    </button>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
                {schemaError && (
                    <div className="mb-4 bg-yellow-900/30 border border-yellow-500/20 text-yellow-400 p-3.5 rounded text-sm flex items-start gap-2.5">
                        <span className="material-symbols-outlined text-yellow-400 text-lg shrink-0">warning</span>
                        <span>{schemaError}</span>
                    </div>
                )}

                {activeTab === 'keys' ? (
                    <div className="space-y-6">
                        {/* Key form */}
                        <form onSubmit={handleAddKey} className="bg-slate-800 p-4 rounded-lg border border-slate-700 grid grid-cols-4 gap-4 items-end">
                            <div className="col-span-1">
                                <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Table</label>
                                <SearchableSelect
                                    value={selectedTableForKeys}
                                    onChange={e => {
                                        setSelectedTableForKeys(e.target.value);
                                        fetchColumns(e.target.value);
                                    }}
                                    className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-white outline-none text-sm"
                                    required
                                    placeholder="-- Choose Table --"
                                    options={tablesList.map(t => ({ value: t, label: t }))}
                                />
                            </div>
                            <div className="col-span-1">
                                <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Column</label>
                                <SearchableSelect
                                    value={newKey.columnName}
                                    onChange={e => setNewKey(prev => ({ ...prev, columnName: e.target.value }))}
                                    className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-white outline-none text-sm"
                                    required
                                    disabled={!selectedTableForKeys}
                                    placeholder="-- Choose Column --"
                                    options={(columnsMap[selectedTableForKeys] || []).map(c => ({ value: c.ColumnName, label: c.ColumnName }))}
                                />
                            </div>
                            <div className="col-span-1 flex space-x-4 py-2">
                                <label className="flex items-center space-x-2 text-sm text-slate-300 cursor-pointer">
                                    <input 
                                        type="checkbox" 
                                        checked={newKey.isPrimaryKey}
                                        onChange={e => setNewKey(prev => ({ ...prev, isPrimaryKey: e.target.checked }))}
                                        className="rounded bg-slate-900 border-slate-750 text-blue-600 focus:ring-0 w-4 h-4"
                                    />
                                    <span>Primary Key</span>
                                </label>
                                <label className="flex items-center space-x-2 text-sm text-slate-300 cursor-pointer">
                                    <input 
                                        type="checkbox" 
                                        checked={newKey.isUnique}
                                        onChange={e => setNewKey(prev => ({ ...prev, isUnique: e.target.checked }))}
                                        className="rounded bg-slate-900 border-slate-750 text-blue-600 focus:ring-0 w-4 h-4"
                                    />
                                    <span>Unique Key</span>
                                </label>
                            </div>
                            <div className="col-span-1">
                                <button 
                                    type="submit"
                                    className="w-full bg-blue-600 hover:bg-blue-500 text-white rounded px-4 py-2 font-medium transition-colors text-sm"
                                >
                                    Add Virtual Key
                                </button>
                            </div>
                        </form>

                        {/* Keys List */}
                        <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-slate-850 border-b border-slate-700 text-slate-400 text-xs font-semibold uppercase tracking-wider">
                                        <th className="px-6 py-3">Table</th>
                                        <th className="px-6 py-3">Column</th>
                                        <th className="px-6 py-3">Key Type</th>
                                        <th className="px-6 py-3 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-700 text-sm">
                                    {virtualKeys.length === 0 ? (
                                        <tr>
                                            <td colSpan="4" className="px-6 py-8 text-center text-slate-500">No virtual keys defined yet.</td>
                                        </tr>
                                    ) : (
                                        virtualKeys.map(key => (
                                            <tr key={key.KeyID} className="hover:bg-slate-750/30">
                                                <td className="px-6 py-3 font-semibold text-slate-200">{key.SchemaName}.{key.TableName}</td>
                                                <td className="px-6 py-3 text-slate-300">{key.ColumnName}</td>
                                                <td className="px-6 py-3">
                                                    {key.IsPrimaryKey && <span className="bg-blue-900/40 text-blue-400 px-2.5 py-0.5 rounded-full text-xs font-medium border border-blue-500/20 mr-2">PK</span>}
                                                    {key.IsUnique && <span className="bg-purple-900/40 text-purple-400 px-2.5 py-0.5 rounded-full text-xs font-medium border border-purple-500/20">Unique</span>}
                                                </td>
                                                <td className="px-6 py-3 text-right">
                                                    <button 
                                                        onClick={() => handleDeleteKey(key.KeyID)}
                                                        className="text-red-400 hover:text-red-300 font-medium text-xs transition-colors"
                                                    >
                                                        Delete
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-6">
                        {/* Relationship Form */}
                        <form onSubmit={handleAddRel} className="bg-slate-800 p-5 rounded-lg border border-slate-700 space-y-4">
                            <h3 className="text-sm font-semibold text-white uppercase tracking-wider mb-2 border-b border-slate-700 pb-2">Define Virtual Relationship</h3>
                            <div className="grid grid-cols-2 gap-6">
                                {/* Source table selection */}
                                <div className="space-y-3">
                                    <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Source (Foreign Key Table)</h4>
                                    <div>
                                        <label className="block text-xs font-medium text-slate-500 mb-1">Table</label>
                                        <SearchableSelect
                                            value={selectedTableForRelsSource}
                                            onChange={e => {
                                                setSelectedTableForRelsSource(e.target.value);
                                                fetchColumns(e.target.value);
                                            }}
                                            className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-white outline-none text-sm"
                                            required
                                            placeholder="-- Choose Source Table --"
                                            options={tablesList.map(t => ({ value: t, label: t }))}
                                        />
                                    </div>
                                </div>

                                {/* Target table selection */}
                                <div className="space-y-3">
                                    <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider flex items-center justify-between">
                                        <span>Target (Primary Key Table)</span>
                                        {suggestedTargetCol && (
                                            <span className="text-[10px] bg-blue-900/60 text-blue-400 px-2 py-0.5 rounded border border-blue-500/10 normal-case font-normal animate-pulse">
                                                Primary Key: {suggestedTargetCol}
                                            </span>
                                        )}
                                    </h4>
                                    <div>
                                        <label className="block text-xs font-medium text-slate-500 mb-1">Table</label>
                                        <SearchableSelect
                                            value={selectedTableForRelsTarget}
                                            onChange={e => {
                                                setSelectedTableForRelsTarget(e.target.value);
                                                fetchColumns(e.target.value);
                                            }}
                                            className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-white outline-none text-sm"
                                            required
                                            placeholder="-- Choose Target Table --"
                                            options={tablesList.map(t => ({ value: t, label: t }))}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Columns Relationship Grid for Multi-column support */}
                            <div className="space-y-2.5 bg-slate-900/40 p-4 rounded border border-slate-750">
                                <div className="flex justify-between items-center flex-wrap gap-2">
                                    <div>
                                        <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Column Mappings</h4>
                                        <p className="text-[11px] text-slate-500 mt-0.5">Map matching columns. For multi-column/compound relationships, add additional column pairs below.</p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setColumnPairs(prev => [...prev, { source: '', target: '' }])}
                                        className="text-blue-400 hover:text-blue-300 text-xs py-1.5 px-3 bg-blue-950/30 rounded border border-blue-500/20 font-semibold transition-colors flex items-center gap-1 shadow-sm"
                                    >
                                        + Add Column Pair
                                    </button>
                                </div>

                                <div className="space-y-2">
                                    {columnPairs.map((pair, idx) => (
                                        <div key={idx} className="flex gap-4 items-center bg-slate-900/30 p-2.5 rounded border border-slate-800">
                                            <div className="flex-1">
                                                <SearchableSelect
                                                    value={pair.source}
                                                    onChange={e => {
                                                        const updated = [...columnPairs];
                                                        updated[idx].source = e.target.value;
                                                        setColumnPairs(updated);
                                                    }}
                                                    className="w-full bg-slate-900 border border-slate-700 rounded px-2.5 py-1.5 text-white outline-none text-xs"
                                                    required
                                                    disabled={!selectedTableForRelsSource}
                                                    placeholder="-- Select Source Column --"
                                                    options={(columnsMap[selectedTableForRelsSource] || []).map(c => ({ value: c.ColumnName, label: c.ColumnName }))}
                                                />
                                            </div>
                                            <span className="text-slate-500 font-semibold text-xs">═ matches ═▶</span>
                                            <div className="flex-1 flex gap-2 items-center">
                                                <SearchableSelect
                                                    value={pair.target}
                                                    onChange={e => {
                                                        const updated = [...columnPairs];
                                                        updated[idx].target = e.target.value;
                                                        setColumnPairs(updated);
                                                    }}
                                                    className="flex-1 bg-slate-900 border border-slate-700 rounded px-2.5 py-1.5 text-white outline-none text-xs"
                                                    required
                                                    disabled={!selectedTableForRelsTarget}
                                                    placeholder="-- Select Target Column --"
                                                    options={(columnsMap[selectedTableForRelsTarget] || []).map(c => ({ value: c.ColumnName, label: c.ColumnName }))}
                                                />
                                                {columnPairs.length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setColumnPairs(prev => prev.filter((_, i) => i !== idx))}
                                                        className="text-red-400 hover:text-red-300 text-xs py-1 px-2.5 bg-red-950/20 rounded border border-red-500/25 transition-colors"
                                                    >
                                                        Remove
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="flex gap-4 items-end pt-2">
                                <div className="flex-1">
                                    <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Relationship Notes / Description</label>
                                    <input 
                                        type="text" 
                                        placeholder="Optional relationship notes..." 
                                        value={description}
                                        onChange={e => setDescription(e.target.value)}
                                        className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm text-white outline-none focus:border-blue-500"
                                    />
                                </div>
                                <button 
                                    type="submit"
                                    className="bg-blue-600 hover:bg-blue-500 text-white rounded px-6 py-2 font-medium transition-colors text-sm shadow shrink-0 h-10"
                                >
                                    Add Virtual Relationship
                                </button>
                            </div>
                        </form>

                        {/* Relationships List */}
                        <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-slate-850 border-b border-slate-700 text-slate-400 text-xs font-semibold uppercase tracking-wider">
                                        <th className="px-6 py-3">Source Column(s)</th>
                                        <th className="px-6 py-3">Link Target(s)</th>
                                        <th className="px-6 py-3">Description</th>
                                        <th className="px-6 py-3 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-700 text-sm">
                                    {relationships.length === 0 ? (
                                        <tr>
                                            <td colSpan="4" className="px-6 py-8 text-center text-slate-500">No virtual relationships defined yet.</td>
                                        </tr>
                                    ) : (
                                        relationships.map(rel => (
                                            <tr key={rel.RelationshipID} className="hover:bg-slate-750/30">
                                                <td className="px-6 py-3 font-semibold text-slate-200">
                                                    {rel.SourceSchema}.{rel.SourceTable}
                                                    <div className="text-slate-400 font-normal mt-0.5">{rel.SourceColumn}</div>
                                                </td>
                                                <td className="px-6 py-3 font-semibold text-blue-400">
                                                    {rel.TargetSchema}.{rel.TargetTable}
                                                    <div className="text-blue-500/70 font-normal mt-0.5">{rel.TargetColumn}</div>
                                                </td>
                                                <td className="px-6 py-3 text-slate-400 italic">
                                                    {rel.Description || '-'}
                                                </td>
                                                <td className="px-6 py-3 text-right">
                                                    <button 
                                                        onClick={() => handleDeleteRel(rel.RelationshipID)}
                                                        className="text-red-400 hover:text-red-300 font-medium text-xs transition-colors"
                                                    >
                                                        Delete
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
